<template>
  <div
    :class="[
      'select-wrapper',
      !disabled && variant,
      { disabled },
      open ? 'open' : 'closed',
      { 'text-center': center }
    ]"
    ref="wrapper"
    @click="handleClick"
    @keydown.up.prevent="handleUp"
    @keydown.down.prevent="handleDown"
    @keydown.space.prevent="handleSpace"
    @keydown.enter="handleEnter"
    @keydown.esc="handleEscape"
    @keydown.tab="open = false"
  >
    <ui-popover :open.sync="open" no-focus>
      <div
        :class="['styled-select', { 'button-like': buttonLike }]"
        slot="reference"
      >
        <div class="value-text">{{ selectedValueText }}</div>
        <ui-input
          :id="id"
          ref="filter"
          :class="['filter-input', { filterable, open }]"
          v-model.trim="filter"
          @input="handleFilterInput"
          @blur="handleBlur"
          :placeholder="activeOptionText"
        />
      </div>
      <ul class="option-list default">
        <li
          v-if="filterable && filter && !selectOptions.length"
          class="option disabled"
        >
          <span class="selected-indicator"></span>
          &minus;
        </li>
        <li
          v-for="option in selectOptions"
          :class="[
            'option',
            {
              active: !option.disabled && option.value === activeValue,
              disabled: option.disabled
            }
          ]"
          :key="option.value"
          @click="selectOption(option)"
          @mousemove="activateOption(option)"
          :data-value="option.value"
          ref="selectOptions"
          :title="option.label || option.value"
        >
          <span class="selected-indicator">
            <icon
              name="check"
              v-if="option.value === value"
              class="selected-indicator-icon"
            />
          </span>
          {{ option.label || option.value }}
        </li>
      </ul>
    </ui-popover>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
export default {
  props: {
    id: { type: String },
    type: { type: String, default: "text" },
    variant: {
      type: String,
      validator: val => ["default", "danger"].includes(val),
      default: "default"
    },
    buttonLike: Boolean,
    placeholder: { type: String, default: "" },
    value: String,
    filterable: Boolean,
    readonly: Boolean,
    required: Boolean,
    disabled: Boolean,
    options: { type: Array, required: true },
    center: Boolean
  },
  data() {
    return {
      disableScrollCancel: null,
      open: false,
      filter: "",
      activeValue: null,
      delayedFilterClear: debounce(this.clearFilter, 1000)
    };
  },
  methods: {
    clearFilter() {
      this.filter = "";
    },
    activateOption(option) {
      if (!option.disabled) {
        this.activeValue = option.value;
      }
    },
    selectActiveOption() {
      this.selectOption(this.activeOption);
    },
    selectOption(option) {
      if (option && !option.disabled) {
        this.$emit("input", option.value);
        this.open = false;
      }
    },
    activeValueNext() {
      let currentIndex = this.selectOptionsEnabled.findIndex(
        ({ value }) => value === this.activeValue
      );
      let nextOption = this.selectOptionsEnabled[currentIndex + 1];
      if (!nextOption) nextOption = this.selectOptionsEnabled[0];
      this.activeValue = nextOption?.value;
    },
    activeValuePrev() {
      let currentIndex = this.selectOptionsEnabled.findIndex(
        ({ value }) => value === this.activeValue
      );
      let nextOption = this.selectOptionsEnabled[currentIndex - 1];
      if (!nextOption) {
        nextOption = this.selectOptionsEnabled[
          this.selectOptionsEnabled.length - 1
        ];
      }
      this.activeValue = nextOption?.value;
    },
    handleFilterInput(filterValue) {
      this.delayedFilterClear.cancel();
      const filter = filterValue.toUpperCase();
      let matchedOption =
        this.selectOptionsEnabled.find(({ label, value }) =>
          (label || value).toUpperCase().startsWith(filter)
        ) ||
        this.selectOptionsEnabled.find(({ label, value }) =>
          (label || value).toUpperCase().includes(filter)
        );

      if (this.open) {
        if (matchedOption) this.activeValue = matchedOption.value;
        if (!this.filterable) this.delayedFilterClear();
      } else {
        if (matchedOption) this.selectOption(matchedOption);
        this.delayedFilterClear();
      }
    },
    handleBlur(event) {
      if (this.open) {
        this.$nextTick(() => event.target.focus());
      }
      this.$emit("blur", event);
    },
    handleClick(event) {
      if (this.open) {
        this.open = false;
      } else {
        // Open dropdown if click was inside (eg. not on outside label)
        const {
          top,
          right,
          bottom,
          left
        } = event.target.getBoundingClientRect();
        const { pageX, pageY } = event;
        if (pageY < top) return;
        if (pageX > right) return;
        if (pageY > bottom) return;
        if (pageX < left) return;
        this.open = true;
        this.activeValue = this.value;
      }
    },
    handleUp() {
      if (this.open) {
        this.activeValuePrev();
      } else {
        this.open = true;
        this.activeValue = this.value;
      }
    },
    handleDown() {
      if (this.open) {
        this.activeValueNext();
      } else {
        this.open = true;
        this.activeValue = this.value;
      }
    },
    handleEscape(event) {
      if (this.open) {
        event.preventDefault();
        this.open = false;
      }
    },
    handleSpace() {
      if (this.open) {
        this.selectActiveOption();
        this.open = false;
      } else {
        this.open = true;
        this.activeValue = this.value;
      }
    },
    async scrollActiveItemIntoView() {
      this.$nextTick(() => {
        this.$refs.selectOptions
          ?.find(el => el.dataset.value === this.activeValue)
          ?.scrollIntoView({
            behavior: "auto",
            block: "nearest",
            inline: "nearest"
          });
      });
    },
    handleEnter(event) {
      if (this.open) {
        event.preventDefault();
        this.open = false;
        this.selectActiveOption();
      }
    },
    handleWindowBlur() {
      this.open = false;
    }
  },
  computed: {
    selectOptionsAll() {
      const options = this.options.concat(); // Must avoid mutations on original object
      if (this.placeholder) {
        options.unshift({ value: "", label: this.placeholder, disabled: true });
      }
      return options;
    },
    selectOptionsFiltered() {
      const filter = this.filter.toUpperCase();
      return this.selectOptionsAll.filter(
        ({ label, value }) =>
          label?.toUpperCase().includes(filter) ||
          value?.toUpperCase().includes(filter)
      );
    },
    selectOptions() {
      return this.filterable
        ? this.selectOptionsFiltered
        : this.selectOptionsAll;
    },
    selectOptionsEnabled() {
      return this.selectOptions.filter(({ disabled }) => !disabled);
    },
    activeOption() {
      return this.selectOptionsEnabled.find(
        ({ value }) => value === this.activeValue
      );
    },
    activeOptionText() {
      return this.activeOption?.label || this.activeOption?.value;
    },
    selectedOption() {
      return this.selectOptionsAll.find(({ value }) => value === this.value);
    },
    selectedValueText() {
      return this.selectedOption?.label || this.selectedOption?.value;
    }
  },
  mounted() {
    window.addEventListener("blur", this.handleWindowBlur);
  },
  beforeDestroy() {
    window.removeEventListener("blur", this.handleWindowBlur);
  },
  watch: {
    activeValue() {
      if (this.activeValue != null) {
        this.scrollActiveItemIntoView();
      }
    },
    open() {
      if (this.open) {
        this.scrollActiveItemIntoView();
      } else {
        this.clearFilter();
        this.$refs.filter.$el.focus();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.select-wrapper {
  position: relative;
  text-align: left;
  user-select: none;

  &::after {
    position: absolute;
    top: 50%;
    right: 0.3rem;
    flex: 0 0 auto;
    width: 0;
    height: 0;
    pointer-events: none;
    content: "";
    border: 0.3125rem solid transparent;
    border-top-color: var(--select-color);
    border-bottom-width: 0;
    transition: border-color 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
    transform: translateY(-50%);
  }
  &:focus-within {
    outline: var(--default-focus-outline);
  }
  &.closed:hover,
  &.closed:focus-within {
    --select-background: var(--select-hover-background);
  }
}

.filter-input {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  cursor: default;
  opacity: 0;
  appearance: none;

  &.filterable.open:focus {
    cursor: initial;
    opacity: 1;
  }
}

.option-list {
  box-sizing: border-box;
  display: block;
  flex: 1 1 auto;
  min-width: 100%;
  max-width: 100vw;
  max-height: 100%;
  padding: 0;
  margin: 0;
  overflow: auto;
  font-size: 1rem;
  color: var(--select-color);
  text-align: left;
  list-style: none;
  background: var(--select-background);
  border: 1px solid var(--select-border-color);
  border-radius: 0.25rem;
}

.option {
  display: flex;
  align-items: center;
  padding: 0 0.5rem 0 0;
  overflow: hidden;
  line-height: 1.5;
  color: var(--select-color);
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  background-color: var(--select-background);
  transition-duration: var(--animation-ultra-fast);
  transition-property: background-color, color;

  &.active {
    color: var(--button-primary-text-color);
    background-color: var(--button-primary-background);
  }
}

.selected-indicator {
  box-sizing: content-box;
  display: block;
  width: 1em;
  height: 1em;
  padding: 0 0.2rem;
}

.selected-indicator-icon {
  display: block;
  width: 100%;
  height: 100%;
}

.styled-select {
  box-sizing: border-box;
  display: block;
  flex: 1 1 auto;
  width: 100%;
  height: var(--input-height);
  padding: var(--input-padding-bordered);
  padding-right: 1rem;
  font-size: 1rem;
  line-height: var(--input-line-height);
  color: var(--select-color);
  cursor: default;
  background: var(--select-background);
  border: var(--input-border);
  border-color: var(--select-border-color);
  border-radius: var(--input-border-radius);
  transition-duration: var(--animation-fast);
  transition-property: border-color, background-color;

  &.button-like {
    font-weight: var(--button-font-weight);
  }
}

.text-center {
  text-align: center;
}

.value-text {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.default {
  --select-background: var(--input-default-background);
  --select-hover-background: var(--button-default-hover-background);
  --select-color: var(--button-default-text-color);
  --select-border-color: var(--input-default-border-color);
}

.danger {
  --select-background: var(--button-danger-background);
  --select-hover-background: var(--button-danger-hover-background);
  --select-color: var(--button-danger-text-color);
  --select-border-color: var(--button-danger-border-color);
}

.disabled {
  --select-background: var(--button-disabled-background);
  --select-hover-background: var(--button-disabled-background);
  --select-color: var(--button-disabled-text-color);
  --select-border-color: var(--input-default-border-color);
}
</style>
